
import { supabase } from '@/integrations/supabase/client';

/**
 * Интерфейс аренды, требующей внимания
 */
export interface AttentionRequiredRental {
  id: string;                // ID аренды
  courierName: string;       // Имя курьера
  expectedEndDate: string | null;   // Ожидаемая дата окончания
  isOverdue: boolean;        // Флаг просрочки
  daysLeft: number;          // Количество дней до/после истечения
  human_number: string;      // Человекочитаемый номер сделки
  amount: number;            // Стоимость аренды (используется как сумма просрочки)
  courier_id: string;        // ID курьера
  phone: string | null;      // Телефон курьера
  status: string;            // Статус аренды ('active' или 'pending')
  avito_id: string | null;   // Avito ID курьера
  telegram_id: string | null; // Telegram ID курьера
}

interface GetAttentionRequiredParams {
  days?: number;
  includeDetails?: boolean;
  format?: 'json' | 'csv';
}

/**
 * Получает список арендаторов, требующих внимания
 */
export async function getAttentionRequiredRentals(params?: GetAttentionRequiredParams) {
  try {
    // Получаем текущую сессию для передачи токена
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;
    
    // Вызов Edge функции
    const { data, error } = await supabase.functions.invoke<AttentionRequiredRental[]>(
      'get-attention-required-rentals',
      {
        method: 'POST',
        body: params || {},
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      }
    );

    if (error) {
      console.error('Ошибка при получении арендаторов, требующих внимания:', error);
      throw error;
    }

    // Логируем полученные данные для отладки
    console.log('Полученные данные из Edge функции:', data?.slice(0, 2));

    // Проверяем и форматируем данные
    const formattedData = (data || []).map(rental => ({
      ...rental,
      // Убедимся, что courierName не undefined
      courierName: rental.courierName || 'Не указан',
      // Проверим формат даты
      expectedEndDate: rental.expectedEndDate || null,
      // Убедимся, что daysLeft число
      daysLeft: typeof rental.daysLeft === 'number' ? rental.daysLeft : 0,
      // Проверим сумму
      amount: typeof rental.amount === 'number' ? rental.amount : 0,
      // Проверим статус
      status: rental.status || 'active',
      // Проверим флаг просрочки
      isOverdue: Boolean(rental.isOverdue),
      // Убедимся, что новые поля корректно обработаны
      avito_id: rental.avito_id || null,
      telegram_id: rental.telegram_id || null
    }));

    console.log('Отформатированные данные арендаторов:', formattedData.slice(0, 2));
    return formattedData;
  } catch (err) {
    console.error('Непредвиденная ошибка при получении арендаторов:', err);
    throw err;
  }
}

/**
 * Получает CSV с арендаторами, требующими внимания
 */
export async function getAttentionRequiredRentalsCSV(days = 2) {
  try {
    // Для прямого скачивания CSV используем URL-подход
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;

    // Получаем URL проекта от Edge функции
    const { data } = await supabase.functions.invoke('get-attention-required-rentals', {
      method: 'POST',
      body: { getUrl: true },
      headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
    });
    
    // Если не удалось получить URL, используем стандартный формат
    const origin = data?.origin || `https://${process.env.NEXT_PUBLIC_SUPABASE_PROJECT_ID}.supabase.co/functions/v1`;

    // Формируем URL для вызова функции
    const functionUrl = `${origin}/get-attention-required-rentals?days=${days}&format=csv`;

    // Выполняем запрос с авторизацией
    const response = await fetch(functionUrl, {
      headers: token ? {
        'Authorization': `Bearer ${token}`,
      } : undefined
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ошибка получения CSV: ${response.status} ${errorText}`);
    }

    // Возвращаем содержимое CSV
    return await response.text();
  } catch (err) {
    console.error('Ошибка при получении CSV:', err);
    throw err;
  }
}
